import React, { useState, useEffect } from 'react';
import { 
  Award, 
  Save, 
  Plus, 
  Trash2, 
  Clock, 
  Edit3, 
  X 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

const DEFAULT_LEAPS = [
  { letter: 'L', title: 'Leadership', description: 'Developing confident decision-makers who inspire and guide others with integrity and vision.', icon: 'crown', color: '#e11d48' },
  { letter: 'E', title: 'Extracurricular', description: 'Exploring passions through clubs, sports, arts, and activities that build skills beyond the classroom.', icon: 'star', color: '#d97706' },
  { letter: 'A', title: 'Achievement', description: 'Pursuing academic excellence and personal growth through dedication, effort, and continuous improvement.', icon: 'trophy', color: '#059669' },
  { letter: 'P', title: 'Participation', description: 'Actively engaging in school life, community events, and collaborative projects that strengthen bonds.', icon: 'handshake', color: '#2563eb' },
  { letter: 'S', title: 'Service', description: 'Contributing to society through meaningful service, empathy, and commitment to making a positive impact.', icon: 'heart', color: '#7c3aed' }
];

export default function StudentLifeLeapsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    programs: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });

  const [pendingPrograms, setPendingPrograms] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [editingProgramIndex, setEditingProgramIndex] = useState(null);
  const [modalProgramData, setModalProgramData] = useState(null);

  const getAuthHeaders = (extra = {}) => {
    const token =
      localStorage.getItem('ph_auth_token') ||
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('ph_auth_token') ||
      sessionStorage.getItem('auth_token');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra
    };
  };

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      let leapsSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        {
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache' })
        }
      );

      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          leapsSection = result.data.find(s => s.section_type === 'student-life-leaps');
        }
      }

      if (!leapsSection) {
        const pubResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
          {
            credentials: 'include',
            headers: getAuthHeaders({ 'Cache-Control': 'no-cache' })
          }
        );
        if (pubResponse.ok) {
          const result = await pubResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            leapsSection = result.data.find(s => s.section_type === 'student-life-leaps');
          }
        }
      }

      if (!leapsSection) throw new Error('LEAPS section not found');

      setSectionId(leapsSection.id);

      const content = leapsSection.content_published || leapsSection.content || {};
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      const enData = parsed.en || parsed;

      const loadedData = {
        kicker: enData.kicker || 'Student Development',
        title: enData.title || 'LEAPS Framework',
        description: enData.description || 'Our holistic approach to student growth encompasses five key dimensions that shape well-rounded learners and responsible global citizens.',
        programs: (Array.isArray(enData.programs) && enData.programs.length > 0)
          ? enData.programs
          : DEFAULT_LEAPS
      };

      const pendingMap = {
        kicker: false,
        title: false,
        description: false
      };
      const pProg = {};

      try {
        const pendingRes = await fetch(`${apiBase}/cms/section/${leapsSection.id}/with-pending`, {
          credentials: 'include',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' })
        });
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          if (pendingData.success && Array.isArray(pendingData.data?.pending_changes)) {
            pendingData.data.pending_changes.forEach(ch => {
              if (ch.status === 'pending' && ch.locale === 'en') {
                if (['kicker', 'title', 'description'].includes(ch.field_id)) {
                  pendingMap[ch.field_id] = true;
                  if (ch.new_value !== null && ch.new_value !== undefined) {
                    loadedData[ch.field_id] = ch.new_value;
                  }
                } else if (ch.field_id.includes('programs.')) {
                  const match = ch.field_id.match(/programs\.(\d+)/);
                  if (match) {
                    const pIdx = parseInt(match[1], 10);
                    pProg[`prog_${pIdx}`] = true;
                    if (ch.new_value !== null && ch.new_value !== undefined) {
                      let parsedVal = ch.new_value;
                      if (typeof parsedVal === 'string' && (parsedVal.startsWith('{') || parsedVal.startsWith('['))) {
                        try { parsedVal = JSON.parse(parsedVal); } catch (e) {}
                      }
                      if (typeof parsedVal === 'object' && parsedVal !== null && loadedData.programs[pIdx]) {
                        loadedData.programs[pIdx] = parsedVal;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[LeapsForm] Error loading pending status:', pErr);
      }

      setFormData(loadedData);
      setOriginalData(JSON.parse(JSON.stringify(loadedData)));
      setPendingFields(pendingMap);
      setPendingPrograms(pProg);
    } catch (err) {
      console.error('[LeapsForm] Load error:', err);
      toast.error('Failed to load LEAPS section');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddProgram = () => {
    const letters = ['L', 'E', 'A', 'P', 'S'];
    const usedLetters = formData.programs.map(p => (p.letter || '').toUpperCase());
    const nextLetter = letters.find(l => !usedLetters.includes(l)) || 'X';

    const newProg = {
      letter: nextLetter,
      title: 'New Dimension',
      description: 'Describe this dimension...',
      icon: 'star',
      color: '#2563eb'
    };
    const newIndex = formData.programs.length;
    setFormData(prev => ({
      ...prev,
      programs: [...prev.programs, newProg]
    }));
    setEditingProgramIndex(newIndex);
    setModalProgramData(newProg);
  };

  const handleDeleteProgram = async (index) => {
    if (pendingPrograms[`prog_${index}`]) {
      toast.info('This dimension has a pending approval');
      return;
    }

    const progToDelete = formData.programs[index];
    if (!window.confirm(`Delete dimension "${progToDelete.title}"?`)) return;

    setSubmitting(prev => ({ ...prev, [`prog_${index}`]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `programs.${index}.delete`,
          field_name: `Delete LEAPS Dimension: ${progToDelete.title}`,
          old_value: progToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Deletion request for "${progToDelete.title}" submitted`);
        setFormData(prev => ({
          ...prev,
          programs: prev.programs.filter((_, i) => i !== index)
        }));
        setPendingPrograms(prev => ({ ...prev, [`prog_${index}`]: true }));
      } else {
        toast.error(result.error || result.message || 'Failed to submit dimension deletion');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`prog_${index}`]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const fieldLabels = {
        kicker: 'Section Kicker',
        title: 'LEAPS Section Title',
        description: 'LEAPS Section Description'
      };

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldLabels[fieldName] || fieldName,
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success('Submitted for approval');
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitProgramModal = async (progIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const prog = modalProgramData;
    if (!prog || !prog.title) {
      toast.warning('Please enter a dimension title');
      return;
    }

    const progKey = `prog_${progIndex}`;
    setSubmitting(prev => ({ ...prev, [progKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `programs.${progIndex}.update`,
            field_name: `Update LEAPS Dimension: ${prog.title}`,
            old_value: originalData.programs[progIndex] || {},
            new_value: prog,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Dimension "${prog.title}" submitted for approval`);

        const updated = [...formData.programs];
        updated[progIndex] = prog;
        setFormData(prev => ({ ...prev, programs: updated }));

        setPendingPrograms(prev => ({ ...prev, [progKey]: true }));
        setEditingProgramIndex(null);
        setModalProgramData(null);
      } else {
        if (response.status === 409) {
          toast.error('This dimension already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [progKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading LEAPS section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Award size={22} />
        </div>
        <div>
          <h2 className="unified-title">LEAPS Framework</h2>
          <p className="unified-subtitle">
            Configure the 5 holistic pillars: Leadership, Extracurricular, Achievement, Participation, and Service
          </p>
        </div>
      </div>

      {/* Field 1: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={80}
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Student Development"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Heading</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={120}
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. LEAPS Framework"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Overview Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength={600}
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="Our holistic approach to student growth encompasses five key dimensions..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: LEAPS Dimensions List */}
      <div className="unified-field-card">
        <div className="unified-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <label className="unified-field-label">LEAPS Dimensions ({formData.programs.length})</label>
            <p className="unified-field-desc">
              Interactive 3D flip cards on the public website displaying the initial letter on the front and details on the back
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddProgram}
          >
            <Plus size={14} /> Add Dimension
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
          {formData.programs.map((prog, idx) => {
            const isPending = pendingPrograms[`prog_${idx}`];
            const letter = prog.letter || (prog.title ? prog.title[0]?.toUpperCase() : '?');

            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderLeft: `5px solid ${prog.color || '#2563eb'}`,
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Big Letter Avatar */}
                <div
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '10px',
                    background: prog.color || '#2563eb',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: '900',
                    fontFamily: 'Georgia, serif',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    flexShrink: 0
                  }}
                >
                  {letter}
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '800', fontSize: '15px', color: '#0f172a' }}>
                      {prog.title || `Dimension #${idx + 1}`}
                    </span>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        background: '#e2e8f0',
                        color: '#334155',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}
                    >
                      Dimension #{idx + 1}
                    </span>
                    {prog.color && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: '#f1f5f9',
                          fontSize: '11px',
                          color: '#475569',
                          fontWeight: '600'
                        }}
                      >
                        <span
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: prog.color,
                            display: 'inline-block'
                          }}
                        />
                        {prog.color}
                      </span>
                    )}
                    {prog.icon && (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: '#e0e7ff',
                          color: '#4338ca',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                      >
                        <i className={`fas ${prog.icon.startsWith('fa-') ? prog.icon : `fa-${prog.icon}`}`} style={{ marginRight: '4px' }} />
                        {prog.icon}
                      </span>
                    )}
                    {isPending && (
                      <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Clock size={10} /> Pending Review
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                    {prog.description || 'No description provided'}
                  </p>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={() => {
                      setEditingProgramIndex(idx);
                      setModalProgramData({ ...prog });
                    }}
                  >
                    <Edit3 size={13} /> Edit
                  </button>
                  {!isPending && (
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                      onClick={() => handleDeleteProgram(idx)}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Dimension Modal */}
      {editingProgramIndex !== null && modalProgramData && (
        <div
          className="unified-modal-overlay"
          onClick={() => {
            setEditingProgramIndex(null);
            setModalProgramData(null);
          }}
        >
          <div className="unified-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="unified-modal-header">
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                Edit Dimension: {modalProgramData.title || `#${editingProgramIndex + 1}`}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setEditingProgramIndex(null);
                  setModalProgramData(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '12px' }}>
                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Letter</label>
                    <input
                      type="text"
                      maxLength={1}
                      value={modalProgramData.letter || ''}
                      onChange={(e) => setModalProgramData(prev => ({ ...prev, letter: e.target.value.toUpperCase() }))}
                      placeholder="L"
                      className="unified-input"
                      style={{ textAlign: 'center', fontWeight: '800', fontSize: '18px' }}
                    />
                  </div>
                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Dimension Title *</label>
                    <input
                      type="text"
                      value={modalProgramData.title || ''}
                      onChange={(e) => setModalProgramData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. Leadership"
                      className="unified-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Card Back Description *</label>
                  <textarea
                    value={modalProgramData.description || ''}
                    onChange={(e) => setModalProgramData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Developing confident decision-makers who inspire and guide others..."
                    className="unified-textarea"
                    rows={4}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Theme Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={modalProgramData.color || '#2563eb'}
                        onChange={(e) => setModalProgramData(prev => ({ ...prev, color: e.target.value }))}
                        style={{ width: '42px', height: '38px', padding: 0, border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={modalProgramData.color || '#2563eb'}
                        onChange={(e) => setModalProgramData(prev => ({ ...prev, color: e.target.value }))}
                        className="unified-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>FontAwesome Icon Key</label>
                    <input
                      type="text"
                      value={modalProgramData.icon || 'star'}
                      onChange={(e) => setModalProgramData(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="e.g. crown, star, trophy, handshake, heart"
                      className="unified-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => {
                  setEditingProgramIndex(null);
                  setModalProgramData(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitProgramModal(editingProgramIndex)}
                disabled={submitting[`prog_${editingProgramIndex}`] || !modalProgramData.title}
              >
                <Save size={14} />
                {submitting[`prog_${editingProgramIndex}`] ? 'Submitting...' : 'Save & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
